<template>
    <AddFileSection
        :id="AddFileFormSectionIds.DOCUMENTS"
        sectionDescription="Add or remove documents to include in this file. Select clients and toggle on eSign for signatures."
        sectionHeading="Send documents"
    >
        <div
            v-if="!addFileStore.isFormDisabled"
            class="mt-3"
        >
            <MultiFileDocumentUpload
                active
                :allowTemplateSelection="false"
                allowDocumentSelection
                :branchId="addFileStore.branch?.id"
                :clients="clientOptions"
                :enableNotificationFlag="false"
                headerText="Added documents"
                headerVariant="plain"
                invertedForm
                :routes="formRoutes"
                :showFirstLevelErrorBanner="false"
                @addClient="scrollToClientsSection"
            />
        </div>
    </AddFileSection>
</template>

<script setup lang="ts">
import type { ServerErrors } from '@/company-portal/add-file/add-file-form-errors.types';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import type { AddFileRoutes, TPSDocument } from '@/company-portal/add-file/add-file.types';
import { AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import type { ClientOptionInput } from '@/components/Views/Company/File/Documents/document-upload-utils';
import { buildAddTpsDocumentParams, mapDocumentErrors } from '@/components/Views/Company/File/Documents/document-upload-utils';
import MultiFileDocumentUpload from '@/components/Views/Company/File/Documents/MultiFileDocumentUpload.vue';
import type { AddFileFormDocumentRoutes } from '@/components/Views/Company/File/file.types';
import { type DocumentUploadErrorResponse } from '@/components/Views/Company/File/file.types';
import { scrollToElement } from '@/shared/utils/browser';
import { useFileClientStore } from '@/stores/company/file-client.store';
import { useFileDocumentStore } from '@/stores/file-document.store';
import { watchImmediate } from '@vueuse/core';
import { computed, watch } from 'vue';

const props = withDefaults(
    defineProps<{
        clientSectionId?: string;
        routes: AddFileRoutes['documentRoutes'];
    }>(),
    {
        clientSectionId: 'clients-section',
    },
);

const fileDocumentStore = useFileDocumentStore();
const addFileStore = useAddFileStore();
const fileClientStore = useFileClientStore();

const formRoutes = computed<AddFileFormDocumentRoutes>(() => {
    return {
        esign_edit: addFileStore.tpsOrder ? props.routes.esignEditVendorRoute : props.routes.esignEditRoute,
    };
});

const clientOptions = computed<ClientOptionInput[]>(() =>
    fileClientStore.availableClientOptions.map(client => ({
        id: client.id,
        value: client.id,
        email: client.email,
        first: client.first,
        last: client.last,
        role: client.role as ClientOptionInput['role'], // assertion: the role types are incompatible between this and the file details client type
    })),
);

const scrollToClientsSection = () => {
    requestAnimationFrame(() => {
        scrollToElement(document.getElementById(props.clientSectionId));
    });
};

watch(
    () => addFileStore.submissionErrors,
    errors => {
        if (!errors) return;
        // assertion: the add-file form's ServerErrorResponse and DocumentUploadErrorResponse
        // share the same structural shape (`{ errors?, message? }`) at this boundary
        const documentErrors = errors as DocumentUploadErrorResponse;
        Array.from(fileDocumentStore.documentConfigs.values()).forEach(config => {
            fileDocumentStore.updateEsignStateInvalidationError(config.documentId, '');
            fileDocumentStore.updateDocumentErrors(config.documentId, mapDocumentErrors(documentErrors, config.formKey));
        });
    },
);

watch(
    () => fileDocumentStore.esignEditError,
    errors => {
        if (!errors) return;

        addFileStore.submissionErrors = {
            message: errors.message,
            errors: errors.errors as ServerErrors, // same shape, but the interfaces dont have a current common inheritance
        };
    },
);

watchImmediate(
    () => addFileStore.tpsOrder,
    tpsOrder => {
        if (!tpsOrder) return;

        tpsOrder.documents?.forEach((tpsDocument: TPSDocument) => {
            fileDocumentStore.addDocument(buildAddTpsDocumentParams(tpsDocument, tpsOrder));
        });
    },
);
</script>
